import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as notesApi from "@/lib/api/notes";
import { useQueryInvalidation } from "@/hooks/useQueryInvalidation";
import { queryKeys } from "@/lib/query-keys";
import type {
  CreateNoteInput,
  UpdateNoteInput,
  GenerateTodoInstructionsInput,
} from "api-schemas";

type CreateNoteWithUserInput = CreateNoteInput & { userId: string };

type LinkMetadataInput = {
  url: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  noteId: string;
};

export function useCreateNoteMutation() {
  const { invalidateNotes } = useQueryInvalidation();

  return useMutation({
    mutationFn: (data: CreateNoteWithUserInput) => notesApi.createNote(data),
    onSuccess: () => {
      invalidateNotes();
    },
  });
}

export function useCreateNote() {
  const { invalidateNotes } = useQueryInvalidation();

  return useMutation({
    mutationFn: (data: { title: string; content?: string; type?: string }) =>
      notesApi.createNote(data),
    onSuccess: () => {
      invalidateNotes();
    },
  });
}

export function useCreateLinkMetadataMutation() {
  const { invalidateNotes } = useQueryInvalidation();

  return useMutation({
    mutationKey: ["link-metadata"],
    mutationFn: async (data: LinkMetadataInput) => {
      return notesApi.createLinkMetadata(data);
    },
    onSuccess: () => {
      invalidateNotes();
    },
  });
}

export function useUpdateNoteMutation() {
  const queryClient = useQueryClient();
  const { invalidateNote } = useQueryInvalidation();

  return useMutation({
    mutationKey: ["update-note"],
    mutationFn: ({
      noteId,
      ...updates
    }: Omit<UpdateNoteInput, "id"> & { noteId: string }) => {
      const normalizedDueDate = updates.dueDate
        ? updates.dueDate instanceof Date
          ? updates.dueDate.toISOString()
          : updates.dueDate
        : updates.dueDate === null
          ? null
          : undefined;

      return notesApi.updateNote({
        id: noteId,
        ...updates,
        dueDate: normalizedDueDate,
      });
    },
    onMutate: async ({ noteId, ...updates }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notes.all });

      const previousNotes = queryClient.getQueriesData({
        queryKey: queryKeys.notes.all,
      });

      queryClient.setQueriesData(
        { queryKey: queryKeys.notes.all },
        (oldData: unknown) => {
          if (!Array.isArray(oldData)) return oldData;

          return oldData.map((note: { id: string }) =>
            note.id === noteId
              ? {
                  ...note,
                  ...updates,
                }
              : note,
          );
        },
      );

      return { previousNotes };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousNotes) {
        for (const [queryKey, data] of context.previousNotes) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: (_data, _error, { noteId }) => {
      invalidateNote(noteId);
    },
  });
}

export function useDeleteNoteMutation() {
  const queryClient = useQueryClient();
  const { invalidateNotes } = useQueryInvalidation();

  return useMutation({
    mutationFn: (noteId: string) => notesApi.deleteNote(noteId),
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notes.all });

      const previousNotes = queryClient.getQueriesData({
        queryKey: queryKeys.notes.all,
      });

      queryClient.setQueriesData(
        { queryKey: queryKeys.notes.all },
        (oldData: unknown) => {
          if (!Array.isArray(oldData)) return oldData;
          return oldData.filter((note: { id: string }) => note.id !== noteId);
        },
      );

      return { previousNotes };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousNotes) {
        for (const [queryKey, data] of context.previousNotes) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      invalidateNotes();
    },
  });
}

export function useGenerateTodoInstructionsMutation() {
  return useMutation({
    mutationKey: ["generateTodoInstructions"],
    mutationFn: async (data: Omit<GenerateTodoInstructionsInput, "noteId">) => {
      return notesApi.generateTodoInstructions(data);
    },
    onSuccess: (_data, variables) => {
      console.log("Generated todo instructions for:", variables.todoText);
    },
    onError: (error) => {
      console.error("Error generating todo instructions:", error);
    },
  });
}

export function useRegenerateInstructionsMutation() {
  const { invalidateNote, invalidateByKey } = useQueryInvalidation();

  return useMutation({
    mutationKey: ["regenerateInstructions"],
    mutationFn: async ({
      noteId,
      noteTitle,
      noteContent,
    }: {
      noteId: string;
      noteTitle?: string | null;
      noteContent?: string | null;
    }) => {
      const textForInstructions = noteContent || noteTitle;
      if (!textForInstructions) {
        throw new Error("No content available to generate instructions");
      }

      return notesApi.generateTodoInstructions({
        todoText: textForInstructions,
        noteId: noteId,
      });
    },
    onSuccess: (_data, { noteId }) => {
      console.log("Regenerated instructions for note:", noteId);
      invalidateNote(noteId);
      invalidateByKey(["noteActivity", noteId]);
    },
    onError: (error) => {
      console.error("Error regenerating instructions:", error);
    },
  });
}

export function useTriageNoteMutation() {
  const updateMutation = useUpdateNoteMutation();
  const queryClient = useQueryClient();

  const moveNote = async (data: {
    noteId: string;
    fromListType?: string;
    toListType: string;
    newStatus?: string | null;
  }) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || "http://localhost:4000"}/api/note-lists/move-note`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) throw new Error("Failed to move note");
    queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
    return response.json();
  };

  return {
    markAsProcessed: async (noteId: string) => {
      await moveNote({
        noteId,
        fromListType: "inbox",
        toListType: "inbox",
        newStatus: "processed",
      });
    },
    markAsArchived: async (noteId: string) => {
      await moveNote({
        noteId,
        fromListType: "inbox",
        toListType: "inbox",
        newStatus: "archived",
      });
    },
    convertToTodo: async (noteId: string) => {
      updateMutation.mutate({
        noteId,
        type: "todo",
        dueDate: new Date(),
      });
      await moveNote({
        noteId,
        fromListType: "inbox",
        toListType: "todos",
      });
    },
    convertToBookmark: async (noteId: string) => {
      updateMutation.mutate({
        noteId,
        type: "bookmark",
      });
      await moveNote({
        noteId,
        fromListType: "inbox",
        toListType: "inbox",
        newStatus: "processed",
      });
    },
  };
}
